import { FormEvent, useState } from "react";

import { CheckboxProps, DropdownProps, Message, Segment, Select } from "semantic-ui-react";

import SettingsSlidersVector from "../../../assets/SettingsSlidersVector";
import { SmartTargetFilter } from "../../../types/types";
import Button from "../../UI/Button/Button";
import Checkbox from "../../UI/Checkbox/Checkbox";

import "./SmartTargetFilterItem.scss";
import Input from "../../UI/Input/Input";

export interface SmartTargetFilterItemProps {
	filterState: SmartTargetFilter;
	setFilterState: (filterState: SmartTargetFilter) => void;
}

/**
 * Available values for the smart target filter operator (now only uses equals) and the if filter operator.
 */
export const smartTargetComparisons = ["<=", "=", ">="] as const;
export type SmartTargetComparison = (typeof smartTargetComparisons)[number];

export default function SmartTargetFilterItem({ filterState, setFilterState }: SmartTargetFilterItemProps) {
	const [isSettingsOpen, setSettingsOpen] = useState<boolean>(false);
	const [error, setError] = useState("");

	const improvement = filterState.customImprovement;
	const improvementName = improvement.name.split("(")[0].trim();

	const onHideToggleChange = (_event: FormEvent, data: CheckboxProps) => {
		hideToggle(!data.checked);
	};

	const onItemClick = (event: MouseEvent) => {
		// prevent from triggering twice or for child buttons
		if (event.target !== event.currentTarget) {
			return;
		}
		hideToggle();
	};

	const hideToggle = (value?: boolean) => {
		if (value == null) {
			value = !filterState.hidden;
		}
		if (value) {
			setSettingsOpen(false);
			if (filterState.included) {
				setFilterState({ ...filterState, included: false, hidden: value });
				return;
			}
		}
		setFilterState({ ...filterState, hidden: value });
	};

	const onMustIncludeToggleChange = (_event: FormEvent, data: CheckboxProps) => {
		if (data.checked == null) {
			data.checked = !filterState.included ?? false;
		}
		if (improvement.accepts_parameters) {
			// prompt for a value too, (recommended but not needed)
			setSettingsOpen(data.checked);
		}
		setFilterState({ ...filterState, included: data.checked });
	};

	const onNumberValueChange = (value: string, type: "value" | "if_value") => {
		setError("");
		if (value !== "") {
			const inputNum = Number(value);
			if (isNaN(inputNum)) {
				// the user shouldn't be able to enter non-numeric, but double check for fun
				setError("Value must be a number");
			}

			if (improvement.min && inputNum < improvement.min) {
				setError(`Value must be between ${improvement.min} and ${improvement.max}`);
			} else if (improvement.max && inputNum > improvement.max) {
				setError(`Value must be between ${improvement.min} and ${improvement.max}`);
			}

			if (type === "if_value" && !filterState.if_operator) {
				// explicitly set operator to the default if it has not already been specified
				setFilterState({ ...filterState, if_operator: smartTargetComparisons[0], if_value: inputNum });
			} else {
				setFilterState({ ...filterState, [type]: inputNum });
			}
		} else {
			setFilterState({ ...filterState, [type]: "" });
		}
	};

	const onIfComparisonChange = (_event: FormEvent, data: DropdownProps) => {
		if (!isComparisonOperator(data.value)) {
			// very unlikely to end up here while using a dropdown for input
			console.error("Invalid comparison operator");
			return;
		}
		setFilterState({ ...filterState, if_operator: data.value });
	};

	const isComparisonOperator = (value: any): value is SmartTargetComparison => {
		return smartTargetComparisons.includes(value as SmartTargetComparison);
	};

	const removeCustomisedTag = (name: string) => (name.includes("(") ? name.split("(")[0].trim() : name);

	return (
		<div
			className={
				"filter-item-container " +
				(isSettingsOpen && !filterState.hidden ? "filter-item-expanded" : "filter-item-collapsed")
			}
		>
			<Segment
				className={
					"filter-item filter-item-upper " + ((!filterState.hidden ?? true) ? "filter-item-selected" : "")
				}
				onClick={onItemClick}
			>
				<Checkbox onChange={onHideToggleChange} checked={!filterState.hidden ?? true} />
				{removeCustomisedTag(improvement.name)}
				<div className="float-right-container">
					<Checkbox
						className={improvement.accepts_parameters ? "" : "settings-slider-padding"}
						toggle
						label="Force"
						labelSide="left"
						disabled={filterState.hidden}
						checked={filterState.included ?? false}
						onChange={onMustIncludeToggleChange}
					/>
					{improvement.accepts_parameters && (
						<Button onClick={() => setSettingsOpen(!isSettingsOpen)} disabled={filterState.hidden}>
							<SettingsSlidersVector />
						</Button>
					)}
				</div>
			</Segment>
			<Segment className={"filter-item-lower"}>
				{improvement.accepts_parameters && (
					<>
						<div>
							{improvementName} parameter =
							<Input
								placeholder={"value"}
								value={filterState.value}
								onChange={(_, data) => onNumberValueChange(data.value, "value")}
								type="number"
							/>
							{improvement.units}
						</div>
						<div>
							Include if parameter is already
							<Select
								options={smartTargetComparisons.map((comparison: SmartTargetComparison) => ({
									key: comparison,
									text: comparison,
									value: comparison,
								}))}
								onChange={onIfComparisonChange}
								inline
								value={filterState.if_operator ?? "<="}
							/>
							<Input
								placeholder={"value"}
								value={filterState.if_value}
								onChange={(_, data) => onNumberValueChange(data.value, "if_value")}
								type="number"
							/>
							{improvement.units}
						</div>
					</>
				)}
			</Segment>
			{error && <Message error>{error}</Message>}
		</div>
	);
}
